/** 0–255 perceived brightness (same weights as legacy getContrastingColor). */
export const getLuminance = (bgColor: string): number => {
  let color = bgColor.trim();

  if (color.startsWith('#')) color = color.slice(1);

  // rgb/rgba(…) from getComputedStyle
  const rgbMatch = color.match(
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i
  );
  if (rgbMatch) {
    const r = Number(rgbMatch[1]);
    const g = Number(rgbMatch[2]);
    const b = Number(rgbMatch[3]);
    return 0.299 * r + 0.587 * g + 0.114 * b;
  }

  if (color.length === 3) {
    color = color
      .split('')
      .map(c => c + c)
      .join('');
  }

  if (color.length < 6 || Number.isNaN(parseInt(color.slice(0, 2), 16))) {
    return 0;
  }

  const r = parseInt(color.slice(0, 2), 16);
  const g = parseInt(color.slice(2, 4), 16);
  const b = parseInt(color.slice(4, 6), 16);

  return 0.299 * r + 0.587 * g + 0.114 * b;
};

const BLACK = '#000000';
const WHITE = '#ffffff';
/** Approx learner page canvas in DarkStyle (--color-white inverted). */
const DARK_SURFACE = '#1a171c';
const LIGHT_SURFACE = '#ffffff';

/** Midpoint used historically for on-accent text (white vs black). */
const ON_ACCENT_THRESHOLD = 150;
/**
 * How far accent luminance must sit from the page surface before we keep
 * brand as focus/link color. Below this → fall back to black/white.
 */
const SURFACE_DELTA = 80;
/** Near-white fills need an edge so the button is visible on a white page. */
const EDGE_THRESHOLD = 220;
/**
 * Mirror of EDGE_THRESHOLD for dark surfaces (≈ 255 − 220). Near-black
 * secondary / page canvas needs a light modal outline in dark theme.
 */
const DARK_EDGE_THRESHOLD = 35;

/**
 * Text/icon color on top of a filled accent button.
 * Light brand → black; dark brand → white.
 */
export const getContrastingColor = (bgColor: string): string =>
  getLuminance(bgColor) > ON_ACCENT_THRESHOLD ? BLACK : WHITE;

const surfaceHex = (surfaceIsDark: boolean) =>
  surfaceIsDark ? DARK_SURFACE : LIGHT_SURFACE;

const contrastsWithSurface = (color: string, surfaceIsDark: boolean) =>
  Math.abs(getLuminance(color) - getLuminance(surfaceHex(surfaceIsDark))) >=
  SURFACE_DELTA;

/**
 * Accent used as *foreground* on the page (links, text accents).
 * Keeps brand when readable; otherwise black (light mode) / white (dark).
 */
export const getAccentForeground = (
  accent: string,
  surfaceIsDark: boolean
): string => {
  if (contrastsWithSurface(accent, surfaceIsDark)) return accent;
  return surfaceIsDark ? WHITE : BLACK;
};

/**
 * Outer focus ring + inner separator (EDS double ring).
 * Prefer brand when it contrasts with the page; else black/white.
 * Inner ring is always the opposite of the outer so the pair never collapses
 * into two identical strokes.
 */
export const getFocusRingColors = (
  accent: string,
  surfaceIsDark: boolean
): { ring: string; inner: string } => {
  const ring = contrastsWithSurface(accent, surfaceIsDark)
    ? accent
    : surfaceIsDark
      ? WHITE
      : BLACK;
  return { ring, inner: getContrastingColor(ring) };
};

const parseRgb = (input: string): [number, number, number] | null => {
  const color = input.trim();
  const rgbMatch = color.match(
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i
  );
  if (rgbMatch) {
    return [Number(rgbMatch[1]), Number(rgbMatch[2]), Number(rgbMatch[3])];
  }
  let hex = color.startsWith('#') ? color.slice(1) : color;
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map(c => c + c)
      .join('');
  }
  if (hex.length < 6) return null;
  const rgb = [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16));
  if (rgb.some(Number.isNaN)) return null;
  return rgb as [number, number, number];
};

/** WCAG 2.x relative luminance (0–1). */
const getRelativeLuminance = (color: string): number => {
  const rgb = parseRgb(color);
  if (!rgb) return 0;
  const [r, g, b] = rgb.map(v => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** WCAG 2.x contrast ratio (1–21). */
export const getContrastRatio = (a: string, b: string): number => {
  const la = getRelativeLuminance(a);
  const lb = getRelativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

/** WCAG 1.4.11 / 2.4.13 minimum for focus indicators. */
const NON_TEXT_CONTRAST = 3;

/**
 * Inner ring of the image-hotspot focus indicator. It sits directly on the
 * hotspot button, whose fill is the theme surface (--color-white), so the
 * brand is kept only when it reaches 3:1 against that fill.
 */
export const getHotspotFocusRing = (
  accent: string,
  surfaceIsDark: boolean
): string => {
  if (getContrastRatio(accent, surfaceHex(surfaceIsDark)) >= NON_TEXT_CONTRAST)
    return accent;
  return surfaceIsDark ? WHITE : BLACK;
};

/** Visible edge on near-white filled buttons (else transparent). */
export const getAccentEdge = (accent: string): string =>
  getLuminance(accent) > EDGE_THRESHOLD ? 'rgba(0, 0, 0, 0.35)' : 'transparent';

/**
 * Border for LlamaModal when brand secondary (--color-accent-lt) sits too
 * close to the modal fill:
 * - light theme + near-white secondary → dark edge
 * - dark theme + near-black secondary → light edge
 * (Modal fill is theme `--color-white`: #fff light / #1A171C dark.)
 */
export const getModalEdge = (
  pageCanvas: string | null | undefined,
  surfaceIsDark: boolean
): string => {
  const canvas =
    pageCanvas?.trim() || (surfaceIsDark ? DARK_SURFACE : LIGHT_SURFACE);
  const lum = getLuminance(canvas);
  if (surfaceIsDark) {
    return lum < DARK_EDGE_THRESHOLD
      ? 'rgba(255, 255, 255, 0.35)'
      : 'transparent';
  }
  return lum > EDGE_THRESHOLD ? 'rgba(0, 0, 0, 0.35)' : 'transparent';
};

export type BrandContrastVars = {
  onAccent: string;
  accentFg: string;
  /** Brand (or black) for icons/text sitting on fixed light chrome (#f8f1ff bars). */
  accentOnLight: string;
  focusRing: string;
  focusRingInner: string;
  focusRingHotspot: string;
  accentEdge: string;
  modalEdge: string;
};

export const computeBrandContrastVars = (
  accentDark: string,
  surfaceIsDark: boolean,
  pageCanvas?: string | null
): BrandContrastVars => {
  const { ring, inner } = getFocusRingColors(accentDark, surfaceIsDark);
  return {
    onAccent: getContrastingColor(accentDark),
    accentFg: getAccentForeground(accentDark, surfaceIsDark),
    // Preview Mode / editor plaques stay light even in dark theme.
    accentOnLight: getAccentForeground(accentDark, false),
    focusRing: ring,
    focusRingInner: inner,
    focusRingHotspot: getHotspotFocusRing(accentDark, surfaceIsDark),
    accentEdge: getAccentEdge(accentDark),
    modalEdge: getModalEdge(pageCanvas, surfaceIsDark)
  };
};

const DEFAULT_ACCENT_DARK = '#9845FB';

/**
 * Writes CAT-1261 contrast tokens onto `:root`.
 * Call after setting brand accents and whenever light/dark surface flips.
 */
export const applyBrandContrastVars = (
  root: CSSStyleDeclaration,
  accentDark: string | null | undefined,
  surfaceIsDark: boolean,
  pageCanvas?: string | null
): BrandContrastVars => {
  const accent = accentDark?.trim() || DEFAULT_ACCENT_DARK;
  const vars = computeBrandContrastVars(accent, surfaceIsDark, pageCanvas);

  root.setProperty('--color-on-accent', vars.onAccent);
  root.setProperty('--color-accent-fg', vars.accentFg);
  root.setProperty('--color-accent-on-light', vars.accentOnLight);
  root.setProperty('--color-focus-ring', vars.focusRing);
  root.setProperty('--color-focus-ring-inner', vars.focusRingInner);
  root.setProperty('--color-focus-ring-hotspot', vars.focusRingHotspot);
  root.setProperty('--color-accent-edge', vars.accentEdge);
  root.setProperty('--color-modal-edge', vars.modalEdge);

  return vars;
};

/**
 * Apply company brand accents + derived contrast tokens.
 * Brand hex stays as-is; only companion tokens adapt for legibility.
 */
export const applyBrandColors = (
  element: HTMLElement,
  options: {
    brandColorDark?: string | null;
    brandColorLight?: string | null;
    surfaceIsDark: boolean;
  }
): void => {
  const { brandColorDark, brandColorLight, surfaceIsDark } = options;
  const root = element.style;

  if (brandColorDark) {
    root.setProperty('--color-accent-dk', brandColorDark);
  }
  if (brandColorLight) {
    root.setProperty('--color-accent-lt', brandColorLight);
  }

  const accent =
    brandColorDark ||
    getComputedStyle(element).getPropertyValue('--color-accent-dk').trim() ||
    DEFAULT_ACCENT_DARK;

  const pageCanvas =
    brandColorLight ||
    getComputedStyle(element).getPropertyValue('--color-accent-lt').trim() ||
    null;

  applyBrandContrastVars(root, accent, surfaceIsDark, pageCanvas);
};
